import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, ChevronLeft, ChevronRight, MapPin, Navigation } from 'lucide-react';
import {
  PannellumPanorama,
  type PannellumViewer,
} from '@ironview/interactive-viewer';
import { apiClient } from '@/lib/api-client';
import { GET } from '@/shared/api/requests';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { SkyMapScene } from './SkyMapScene';
import { SkyMapPanoramaMosaic } from './SkyMapPanoramaMosaic';
import {
  isSkyMapPanoramaNavigation,
  type SkyMapMatrix3,
  type SkyMapPanoramaNavigation,
} from './skyMapPanoramaNavigation';
import 'pannellum/build/pannellum.css';
import 'pannellum/build/pannellum.js';
import '@ironview/interactive-viewer/styles.css';
import './skyMapPanoramaNavigation.css';

interface GpsPoint { time: number; lat: number; lng: number; elevationM: number | null }
interface GpsPath { durationSeconds: number; points: GpsPoint[] }
export interface SkyMapNode {
  id: string;
  frame: number;
  timeSeconds: number;
  position: [number, number, number];
  smoothedPosition: [number, number, number];
  reconstructionPosition?: [number, number, number];
  worldToPanoramaRotation?: SkyMapMatrix3;
  image: string;
  panorama?: string;
}
interface SkyMapPresentation {
  panoramaPitch?: number;
  fitDirection?: [number, number, number];
  pathPresentation?: 'overlay' | 'occluded';
}
interface SkyMapPath {
  version: number;
  kind: 'ironview.sky-map.flight-path';
  source: { registeredFrames: number; durationSeconds: number };
  presentation?: SkyMapPresentation;
  nodes: SkyMapNode[];
}
type FlightPathData = GpsPath | SkyMapPath;
interface Props {
  videoId: number;
  currentTime: number;
  onSeek: (time: number) => void;
  displayMode?: 'panel' | 'viewer';
}

const isSkyMap = (data: FlightPathData): data is SkyMapPath =>
  'kind' in data && data.kind === 'ironview.sky-map.flight-path';

const formatTime = (seconds: number) => {
  const value = Math.max(0, Math.round(seconds));
  return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
};

const Grid = () => (
  <path d="M0 25H100M0 50H100M0 75H100M25 0V100M50 0V100M75 0V100" stroke="currentColor" strokeOpacity=".08" strokeWidth=".35" />
);

const Header: React.FC<{ skyMap?: boolean }> = ({ skyMap = false }) => (
  <div className="border-b border-border px-5 py-4">
    <div className="flex items-center justify-between gap-3 text-foreground">
      <div className="flex items-center gap-2">
        <Navigation className="size-4 text-primary" />
        <h2 className="font-display text-sm font-bold tracking-[-0.01em]">Flight path</h2>
      </div>
      {skyMap && <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] text-primary">3D</span>}
    </div>
    <p className="mt-1 text-xs text-muted-foreground">
      {skyMap ? 'Drag to orbit. Select a camera node to inspect its still.' : 'Select a node to jump through the recording.'}
    </p>
  </div>
);

const AuthenticatedPanorama: React.FC<{
  endpoint: string;
  node: SkyMapNode;
  nodes: readonly SkyMapNode[];
  navigation: SkyMapPanoramaNavigation | null;
  pitch: number;
  onNavigate: (node: SkyMapNode) => void;
}> = ({ endpoint, node, nodes, navigation, pitch, onNavigate }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [loadError, setLoadError] = useState(false);
  const viewerRef = useRef<PannellumViewer | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl = '';
    setImageUrl('');
    setLoadError(false);

    apiClient.get<Blob>(endpoint, {
      responseType: 'blob',
      signal: controller.signal,
    }).then((response) => {
      if (controller.signal.aborted) return;
      objectUrl = URL.createObjectURL(response.data);
      setImageUrl(objectUrl);
    }).catch(() => {
      if (!controller.signal.aborted) setLoadError(true);
    });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [endpoint]);

  if (loadError) {
    return (
      <div className="grid min-h-80 place-items-center bg-[#07101a] p-8 text-sm text-slate-300">
        This panorama could not be loaded.
      </div>
    );
  }
  if (!imageUrl) {
    return (
      <div className="grid min-h-80 place-items-center bg-[#07101a] p-8 text-sm text-slate-300">
        Loading panorama…
      </div>
    );
  }
  return (
    <div className="relative w-full bg-[#07101a]">
      <PannellumPanorama
        sceneId={node.id}
        imageUrl={imageUrl}
        target={{ pitch, yaw: 0, duration: 0 }}
        viewerRef={viewerRef}
        className="iv-panorama__container w-full bg-[#07101a]"
        style={{ height: 'min(66vh, 42rem)' }}
        onError={(panoramaError) =>
          console.error('[DroneSkyMap] Panorama error:', panoramaError)
        }
      />
      {navigation && (
        <SkyMapPanoramaMosaic
          viewerRef={viewerRef}
          nodes={nodes}
          currentNode={node}
          navigation={navigation}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
};

const GpsPanel: React.FC<{ path: GpsPath; currentTime: number; onSeek: (time: number) => void }> = ({ path, currentTime, onSeek }) => {
  const plot = useMemo(() => {
    const minLng = Math.min(...path.points.map((point) => point.lng));
    const maxLng = Math.max(...path.points.map((point) => point.lng));
    const minLat = Math.min(...path.points.map((point) => point.lat));
    const maxLat = Math.max(...path.points.map((point) => point.lat));
    const projected = path.points.map((point) => ({
      x: 10 + ((point.lng - minLng) / Math.max(maxLng - minLng, 0.00001)) * 80,
      y: 90 - ((point.lat - minLat) / Math.max(maxLat - minLat, 0.00001)) * 80,
    }));
    const activeIndex = path.points.reduce((closest, point, index) =>
      Math.abs(point.time - currentTime) < Math.abs(path.points[closest].time - currentTime) ? index : closest, 0);
    return { projected, activeIndex };
  }, [currentTime, path]);
  const active = path.points[plot.activeIndex];
  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-border bg-card">
      <Header />
      <div className="p-5">
        <div className="overflow-hidden rounded-xl border border-border bg-muted/45">
          <svg viewBox="0 0 100 100" className="block aspect-square w-full" role="img" aria-label="Drone GPS flight path">
            <Grid />
            <polyline points={plot.projected.map((point) => `${point.x},${point.y}`).join(' ')} fill="none" stroke="currentColor" className="text-primary" strokeWidth="1.35" strokeLinejoin="round" strokeLinecap="round" />
            {path.points.map((point, index) => (index === 0 || index === path.points.length - 1 || point.time % 10 < 2) && (
              <circle key={point.time} cx={plot.projected[index].x} cy={plot.projected[index].y} r="2.15" className="cursor-pointer fill-card stroke-primary hover:fill-primary" strokeWidth="1" onClick={() => onSeek(point.time)} />
            ))}
            <circle cx={plot.projected[plot.activeIndex].x} cy={plot.projected[plot.activeIndex].y} r="3.3" className="fill-primary stroke-primary-foreground" strokeWidth="1.1" />
          </svg>
        </div>
        <div className="mt-4 rounded-lg border border-border bg-muted/50 p-3 text-xs">
          <div className="flex items-center justify-between text-muted-foreground"><span>Current position</span><span>{formatTime(currentTime)}</span></div>
          <div className="mt-2 flex items-center gap-2 text-foreground"><MapPin className="size-3.5 text-primary" /><span>{active.lat.toFixed(6)}, {active.lng.toFixed(6)}</span></div>
          {active.elevationM !== null && <p className="mt-1 pl-5 text-muted-foreground">Elevation {Math.round(active.elevationM)} m</p>}
        </div>
      </div>
    </aside>
  );
};

export const FlightPathPanel: React.FC<Props> = ({ videoId, currentTime, onSeek, displayMode = 'panel' }) => {
  const [flightPath, setFlightPath] = useState<FlightPathData | null>(null);
  const [selectedNode, setSelectedNode] = useState<SkyMapNode | null>(null);
  const [panoramaNavigation, setPanoramaNavigation] = useState<SkyMapPanoramaNavigation | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFlightPath(null);
    setSelectedNode(null);
    setError(false);
    GET(`/drones/${videoId}/flight-path/`)
      .then((response) => response.data as FlightPathData)
      .then((data) => {
        const hasPath = isSkyMap(data) ? data.nodes?.length > 1 : data.points?.length > 1;
        if (!cancelled && hasPath) {
          setFlightPath(data);
        }
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [displayMode, videoId]);

  useEffect(() => {
    const controller = new AbortController();
    setPanoramaNavigation(null);
    if (!flightPath || !isSkyMap(flightPath)) return () => controller.abort();

    apiClient.get<unknown>(`/drones/${videoId}/flight-path/panorama-navigation/`, {
      signal: controller.signal,
    }).then((response) => {
      if (!controller.signal.aborted && isSkyMapPanoramaNavigation(response.data)) {
        setPanoramaNavigation(response.data);
      }
    }).catch((reason: unknown) => {
      if (controller.signal.aborted) return;
      const status = (reason as { response?: { status?: number } })?.response?.status;
      if (status !== 404) {
        console.error('[DroneSkyMap] Panorama navigation could not be loaded.', reason);
      }
    });
    return () => controller.abort();
  }, [flightPath, videoId]);

  if (error) {
    return displayMode === 'viewer'
      ? <div className="grid flex-1 place-items-center bg-background p-8 text-sm text-muted-foreground">This Sky Map could not be loaded.</div>
      : null;
  }
  if (!flightPath) {
    return displayMode === 'viewer'
      ? <div className="grid flex-1 place-items-center bg-background p-8 text-sm text-muted-foreground">Loading Sky Map…</div>
      : <aside className="w-80 border-l border-border bg-card p-5 text-sm text-muted-foreground">Loading flight path…</aside>;
  }
  if (!isSkyMap(flightPath)) return <GpsPanel path={flightPath} currentTime={currentTime} onSeek={onSeek} />;

  const frameUrl = selectedNode ? `/api/v1/drones/${videoId}/flight-path/frames/${encodeURIComponent(selectedNode.id)}/` : '';
  const panoramaEndpoint = selectedNode ? `/drones/${videoId}/flight-path/panoramas/${encodeURIComponent(selectedNode.id)}/` : '';
  const selectedIndex = selectedNode ? flightPath.nodes.findIndex((node) => node.id === selectedNode.id) : -1;
  const selectAdjacent = (offset: number) => {
    if (selectedIndex < 0) return;
    const nextIndex = Math.min(flightPath.nodes.length - 1, Math.max(0, selectedIndex + offset));
    setSelectedNode(flightPath.nodes[nextIndex]);
  };

  if (displayMode === 'viewer') {
    const panoramaPitch = flightPath.presentation?.panoramaPitch ?? 0;
    return (
      <section className="flex min-h-0 flex-1 bg-background p-4">
        <SkyMapScene
          videoId={videoId}
          nodes={flightPath.nodes}
          selectedNodeId={selectedNode?.id ?? null}
          onSelectNode={setSelectedNode}
          fitDirection={flightPath.presentation?.fitDirection}
          pathPresentation={flightPath.presentation?.pathPresentation}
        />
        <Dialog
          open={selectedNode !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedNode(null);
          }}
        >
          <DialogContent
            className="max-w-5xl gap-0 overflow-hidden p-0"
            closeButtonClassName="right-4 top-4"
          >
            {selectedNode && (
              <>
                <DialogHeader className="border-b border-border px-6 py-5 pr-16 text-left">
                  <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    Selected camera
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <DialogTitle className="flex items-center gap-2 text-xl">
                      <MapPin className="size-5 text-primary" strokeWidth={1.9} />
                      Node {selectedNode.frame}
                    </DialogTitle>
                    <span className="rounded-full bg-primary/15 px-2.5 py-1 text-xs font-bold text-primary">
                      {formatTime(selectedNode.timeSeconds)}
                    </span>
                  </div>
                  <DialogDescription>
                    {selectedNode.panorama
                      ? 'Drag to explore this 360° capture.'
                      : 'Calibrated still captured at this point in the flight.'}
                  </DialogDescription>
                </DialogHeader>

                <figure className="bg-[#07101a]">
                  {selectedNode.panorama ? (
                    <AuthenticatedPanorama
                      endpoint={panoramaEndpoint}
                      node={selectedNode}
                      nodes={flightPath.nodes}
                      navigation={panoramaNavigation}
                      pitch={panoramaPitch}
                      onNavigate={setSelectedNode}
                    />
                  ) : (
                    <img
                      src={frameUrl}
                      alt={`Drone still from frame ${selectedNode.frame}`}
                      className="max-h-[66vh] w-full bg-[#07101a] object-contain"
                    />
                  )}
                </figure>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-card px-5 py-4">
                  <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
                    <Camera className="size-3.5 text-primary" strokeWidth={1.9} />
                    {selectedNode.panorama
                      ? 'Interactive 360° panorama'
                      : `Calibrated still · Frame ${selectedNode.frame}`}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      disabled={selectedIndex <= 0}
                      onClick={() => selectAdjacent(-1)}
                    >
                      <ChevronLeft className="mr-1.5 size-4" />
                      Previous
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={selectedIndex < 0 || selectedIndex >= flightPath.nodes.length - 1}
                      onClick={() => selectAdjacent(1)}
                    >
                      Next
                      <ChevronRight className="ml-1.5 size-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </section>
    );
  }

  return (
    <aside className="flex w-[42rem] max-w-[50vw] shrink-0 flex-col overflow-y-auto border-l border-border bg-card">
      <Header skyMap />
      <div className="flex flex-1 flex-col p-5">
        <SkyMapScene videoId={videoId} nodes={flightPath.nodes} selectedNodeId={selectedNode?.id ?? null} onSelectNode={setSelectedNode} />
        <div className="mt-4 flex items-center justify-between rounded-lg border border-border bg-muted/50 p-3 text-xs">
          <span className="text-muted-foreground">Recovered cameras</span>
          <span className="font-display font-bold text-foreground">{flightPath.source.registeredFrames}</span>
        </div>
        {selectedNode ? (
          <figure className="mt-4 overflow-hidden rounded-xl border border-border bg-muted/50">
            <img src={frameUrl} alt={`Drone still from frame ${selectedNode.frame}`} className="aspect-video w-full bg-background object-contain" />
            <figcaption className="flex items-center justify-between gap-3 px-3 py-2.5 text-xs">
              <span className="flex items-center gap-2 font-semibold text-foreground"><Camera className="size-3.5 text-primary" />Frame {selectedNode.frame}</span>
              <span className="text-muted-foreground">{formatTime(selectedNode.timeSeconds)}</span>
            </figcaption>
          </figure>
        ) : (
          <div className="mt-4 rounded-xl border border-dashed border-border p-4 text-center text-xs text-muted-foreground">Select a camera node to display its still image.</div>
        )}
      </div>
    </aside>
  );
};
